import React, { useMemo } from 'react';
import {
  getOptions,
  policyType,
  rentersPolicyAvailable,
  allowInstallPayOnHomePoliciesStates,
  paymentType,
  onlyPayInFullOnAutoPoliciesStates
} from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { filterAutoDirectBillOptions } from 'core/helpers/auto-direct-bill';
import withDatePicker from 'core/components/with-date-picker';
import { useStore } from 'core/store/store.mobx';
import useBillingDayOfMonthOptions from 'core/hooks/use-billing-day-of-month-options';
import HomeSettings from './home-settings';
import CondoSettings from './condo-settings';
import AutoSettings from './auto-settings';
import RentersSettings from './renters-settings';
import FarmSettings from './farm-settings';
import OfferSource from './offer-source';
import OfferConfiguration from './offer-configuration';

const Settings = () => {
  const { offer: store } = useStore();

  const hasMonolineHomeOption = store.offer.options.some((option) => option.type === policyType.Home);
  const hasMonolineAutoOption = store.offer.options.some((option) => option.type === policyType.Auto);
  const hasMonolineRentersOption = store.offer.options.some((option) => option.type === policyType.Renters);
  const hasMonolineCondoOption = store.offer.options.some((option) => option.type === policyType.Condo);
  const farmOption = store.offer.options.find((option) => isFarmBasedPolicyType(option.type));

  const homeBillingDayOptions = useBillingDayOfMonthOptions(
    store.offer,
    hasMonolineHomeOption ? policyType.Home : policyType.HABundle
  );
  const autoBillingDayOptions = useBillingDayOfMonthOptions(
    store.offer,
    hasMonolineAutoOption ? policyType.Auto : policyType.HABundle
  );
  const rentersBillingDayOptions = useBillingDayOfMonthOptions(
    store.offer,
    hasMonolineRentersOption ? policyType.Renters : policyType.ARBundle
  );
  const condoBillingDayOptions = useBillingDayOfMonthOptions(
    store.offer,
    hasMonolineCondoOption ? policyType.Condo : policyType.CABundle
  );
  const farmBillingDayOptions = useBillingDayOfMonthOptions(
    store.offer,
    farmOption ? farmOption.type : policyType.FABundle
  );

  const showRenters =
    rentersPolicyAvailable[store.offer?.quote?.correctedAddress?.state] &&
    store.offer.options.some((o) => o.type === policyType.ARBundle || o.type === policyType.Renters) &&
    [policyType.ARBundle, policyType.Renters].includes(store.selectedOption);

  const {
    paymentTypeOptions,
    homePaymentTypeOptions,
    autoPaymentTypeOptions,
    rentersPaymentTypeOptions,
    hPaymentMethodOptions,
    aPaymentMethodOptions,
    rPaymentMethodOptions,
    cPaymentMethodOptions,
    leadSourceOptions,
    fPaymentTypeOptions,
    fPaymentMethodOptions
  } = useMemo(() => {
    const isMIMonolineAuto = store.state === 'MI' && store.selectedOption === policyType.Auto;
    return {
      paymentTypeOptions: getOptions('paymentType', store.state),
      homePaymentTypeOptions: allowInstallPayOnHomePoliciesStates[store.offer?.quote?.correctedAddress?.state]
        ? getOptions('paymentType', store.state)
        : getOptions('homeNewBusinessPaymentType', store.state),
      autoPaymentTypeOptions:
        onlyPayInFullOnAutoPoliciesStates[store.state] || isMIMonolineAuto
          ? getOptions('autoPaymentType', store.state).filter((opt) => opt.id === paymentType.OneTime)
          : getOptions('autoPaymentType', store.state),
      rentersPaymentTypeOptions: getOptions('rentersPaymentType', store.state),
      hPaymentMethodOptions: getOptions('homeownersPaymentMethod', store.state),
      cPaymentMethodOptions: getOptions('condoPaymentMethod', store.state),
      // Direct ('D') is a mutual-only auto payment method; hide it for Branch/BIX (#35123 + this fix).
      aPaymentMethodOptions: filterAutoDirectBillOptions(getOptions('autoPaymentMethod')),
      rPaymentMethodOptions: getOptions('rentersPaymentMethod', store.state),
      leadSourceOptions: getOptions('leadSources'),
      fPaymentTypeOptions: getOptions('farmPaymentType', store.state),
      fPaymentMethodOptions: getOptions('farmPaymentMethod', store.state)
    };
  }, [store]);

  return (
    <>
      {store.shouldShowHomeTab && (
        <HomeSettings
          paymentMethodOptions={hPaymentMethodOptions}
          billingDaysOptions={homeBillingDayOptions}
          paymentTypeOptions={homePaymentTypeOptions}
        />
      )}
      {store.shouldShowCondoTab && (
        <CondoSettings
          paymentMethodOptions={cPaymentMethodOptions}
          billingDaysOptions={condoBillingDayOptions}
          paymentTypeOptions={paymentTypeOptions}
        />
      )}
      {store.shouldShowAutoTab && (
        <AutoSettings
          paymentMethodOptions={aPaymentMethodOptions}
          billingDaysOptions={autoBillingDayOptions}
          paymentTypeOptions={autoPaymentTypeOptions}
        />
      )}
      {showRenters && (
        <RentersSettings
          paymentMethodOptions={rPaymentMethodOptions}
          billingDaysOptions={rentersBillingDayOptions}
          paymentTypeOptions={rentersPaymentTypeOptions}
        />
      )}
      {store.shouldShowFarmTab && (
        <FarmSettings
          paymentMethodOptions={fPaymentMethodOptions}
          billingDaysOptions={farmBillingDayOptions}
          paymentTypeOptions={fPaymentTypeOptions}
        />
      )}
      <OfferSource leadSourceOptions={leadSourceOptions} />
      <OfferConfiguration />
    </>
  );
};

export default withDatePicker(Settings);
